import { Stack, Typography } from "@mui/material";
import { MRT_ColumnDef, MRT_RowData } from "material-react-table";
import { FC, useMemo } from "react";

import { CollapsibleCard } from "@/components/elements/CollapsibleCard";
import { EMPTY_STR } from "@/constants/string";
import { Table } from "@/modules/irohana-components/elements/Table";
import { useTable } from "@/modules/irohana-components/hooks/useTable";
import { PRICE_DIMENSION, PRICE_DIMENSION_LABEL } from "@/features/organization/product/constants/productEnums";
import { PriceSummaryBar } from "@/features/organization/product/components/PriceSummaryBar";
import { TPriceCombination, TProductDetail } from "@/api/services/main/organization/product/dtos/product.dto";
import { formatYenPerName, getVariantTypeLabel, resolveComboPriceForDetail, usesOtherDimension } from "@/features/organization/product/utils/productUtils";
import { useProductMasters } from "@/features/organization/product/hooks/useProductMasters";
import { text } from "@/theme/colors";

type TProductCombinationViewProps = {
  product: TProductDetail;
};

type TCombinationViewRow = TPriceCombination & { computedUnitPrice: number | null };

const subtotal = (row: TCombinationViewRow): number | null =>
  row.computedUnitPrice == null ? null : row.computedUnitPrice - (row.discountTokutei2 ?? 0);

const moneyCol = <TRow extends MRT_RowData>(id: string, header: string): MRT_ColumnDef<TRow> => ({
  id,
  header,
  size: 150,
  muiTableHeadCellProps: { align: "left" },
  muiTableBodyCellProps: { align: "right" },
});

/** 単価組み合わせ条件設定 — summary bar (C8) + read-only table (商品詳細). */
export const ProductCombinationView: FC<TProductCombinationViewProps> = ({ product }) => {
  const { getNationalityName, getIndustryName } = useProductMasters();
  // Third column mirrors the 種別単価 dimension: その他 for 人材紹介料 (C3), otherwise ティア when the
  // product is a tier target. Hidden when neither (just 国籍 / 分野).
  const isOtherDimension = usesOtherDimension(product.name);
  const showThirdColumn = isOtherDimension || product.isTierDiscountTarget;
  const thirdHeader = isOtherDimension
    ? PRICE_DIMENSION_LABEL[PRICE_DIMENSION.OTHER]
    : PRICE_DIMENSION_LABEL[PRICE_DIMENSION.TIER];

  const combinationRows = useMemo<TCombinationViewRow[]>(() => {
    const enabled = {
      nationality: product.nationalityEnabled,
      field: product.fieldEnabled,
      tier: product.variantEnabled,
    };
    const thirdDim = isOtherDimension ? PRICE_DIMENSION.OTHER : PRICE_DIMENSION.TIER;
    return product.combinations.map((row) => ({
      ...row,
      computedUnitPrice: resolveComboPriceForDetail(product.typePrices, row, enabled, thirdDim),
    }));
  }, [product.combinations, product.typePrices, product.nationalityEnabled, product.fieldEnabled, product.variantEnabled, isOtherDimension]);

  const columns = useMemo<MRT_ColumnDef<TCombinationViewRow>[]>(
    () => [
      {
        id: "nationality",
        header: PRICE_DIMENSION_LABEL[PRICE_DIMENSION.NATIONALITY],
        size: 88,
        Cell: ({ row }) => (row.original.nationalityId == null ? EMPTY_STR.TEXT : getNationalityName(row.original.nationalityId)),
      },
      {
        id: "field",
        header: PRICE_DIMENSION_LABEL[PRICE_DIMENSION.FIELD],
        size: 100,
        Cell: ({ row }) => (row.original.fieldId == null ? EMPTY_STR.TEXT : getIndustryName(row.original.fieldId)),
      },
      ...(showThirdColumn
        ? [{
            id: "third",
            header: thirdHeader,
            size: 60,
            Cell: ({ row }: { row: { original: TCombinationViewRow } }) =>
              getVariantTypeLabel(product.typePrices, row.original.variantTypeId),
          }]
        : []),
      {
        ...moneyCol("unitPrice", "単価"),
        size: 60,
        Cell: ({ row }) => formatYenPerName(row.original.computedUnitPrice),
      },
      {
        id: "discount",
        header: "特定技能2号値引き",
        muiTableBodyCellProps: { align: "right" },
        size: 150,
        Cell: ({ row }) => formatYenPerName(row.original.discountTokutei2),
      },
      {
        ...moneyCol("subtotal", "小計"),
        size: 60,
        Cell: ({ row }) => formatYenPerName(subtotal(row.original)),
      },
      {
        id: "memo",
        header: "メモ",
        Cell: ({ row }) => row.original.memo || EMPTY_STR.TEXT,
      },
    ],
    [showThirdColumn, thirdHeader, getNationalityName, getIndustryName, product.typePrices],
  );

  const { table } = useTable({ rows: combinationRows, columns });

  return (
    <CollapsibleCard
      title={
        <Typography variant="body14Bold" color={text.primary}>
          単価組み合わせ条件設定
        </Typography>
      }
      description={<>
        国籍・分野・ティアを組み合わせた単価を設定できます。条件は個別にON/OFFで切り替えられます。
        <br />
        ONにした条件のみ絞り込みに使用されます。OFFの条件は「全て」として扱われます。
      </>}
      showBorder={false}
      sx={{ pt: 0 }}
    >
      <Stack gap={2}>
        <PriceSummaryBar basePrice={product.unitPrice} discount={product.discountTokutei2} />
        <Table table={table} hiddenAddBtn />
      </Stack>
    </CollapsibleCard>
  );
};
